/**
 * Schema registry for derived-node ref-rewriting (EID-47).
 *
 * Builds an `allSchemas` map keyed by short name (e.g. `node/world`),
 * matching the regex used by `walkReferences` in `x-reference.ts`.
 * The synthetic `derivedNodeSchema` is a `oneOf` of every node type the
 * cloned subtree could be — the walker uses the data's `nodeType`
 * discriminator to pick the right branch.
 *
 * Kept out of `derive.ts` so the resolver itself stays Solid- and
 * schemas-package-agnostic and remains easy to unit-test in isolation.
 */

import {
  common_schema,
  data_schema,
  document_schema,
  feature_schema,
  geojson_schema,
  grid_schema,
  gridded_schema,
  label_schema,
  layer_schema,
  menu_schema,
  panel_schema,
  plot_schema,
  scenegraph_schema,
  seasurface_schema,
  theme_schema,
  track_schema,
  vega_schema,
  wmts_schema,
  world_schema,
} from 'schemas';

import type { JsonSchema } from './x-reference';

const SCHEMA_ID_RE = /^https:\/\/schemas\.oceanum\.io\/eidos\/(.+?)\.json$/;

const REGISTERED: Record<string, unknown>[] = [
  common_schema,
  data_schema,
  document_schema,
  feature_schema,
  geojson_schema,
  grid_schema,
  gridded_schema,
  label_schema,
  layer_schema,
  menu_schema,
  panel_schema,
  plot_schema,
  scenegraph_schema,
  seasurface_schema,
  theme_schema,
  track_schema,
  vega_schema,
  wmts_schema,
  world_schema,
];

export const derivedAllSchemas: Record<string, JsonSchema> = (() => {
  const out: Record<string, JsonSchema> = {};
  for (const s of REGISTERED) {
    const id = (s as { $id?: string }).$id;
    if (typeof id !== 'string') continue;
    const m = id.match(SCHEMA_ID_RE);
    if (!m) continue;
    out[m[1]] = s as JsonSchema;
  }
  return out;
})();

/**
 * Synthetic schema describing "any node type that can appear inside a
 * container slot". Excludes `derived` (a derived source can't itself be
 * derived) and `panel` (panels live as siblings under the eidos root,
 * not inside a derived subtree's content).
 *
 * Worldlayer types are listed too: a derived node's source could be a
 * world, whose children include worldlayer subtypes. The walker picks
 * the right oneOf branch based on the data's `nodeType` / `layerType`
 * discriminator.
 */
export const derivedNodeSchema: JsonSchema = {
  oneOf: [
    { $ref: 'https://schemas.oceanum.io/eidos/node/world.json' },
    { $ref: 'https://schemas.oceanum.io/eidos/node/plot.json' },
    { $ref: 'https://schemas.oceanum.io/eidos/node/document.json' },
    { $ref: 'https://schemas.oceanum.io/eidos/node/grid.json' },
    { $ref: 'https://schemas.oceanum.io/eidos/node/menu.json' },
    { $ref: 'https://schemas.oceanum.io/eidos/node/worldlayer/feature.json' },
    { $ref: 'https://schemas.oceanum.io/eidos/node/worldlayer/gridded.json' },
    { $ref: 'https://schemas.oceanum.io/eidos/node/worldlayer/label.json' },
    {
      $ref: 'https://schemas.oceanum.io/eidos/node/worldlayer/scenegraph.json',
    },
    {
      $ref: 'https://schemas.oceanum.io/eidos/node/worldlayer/seasurface.json',
    },
    { $ref: 'https://schemas.oceanum.io/eidos/node/worldlayer/track.json' },
    { $ref: 'https://schemas.oceanum.io/eidos/node/worldlayer/wmts.json' },
  ],
};
